/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Manages passport resources (battery digital product passports).
 *
 * Features:
 * - `loadOne$()` — fetches a single passport by ID and populates internal
 *   edit/cache signals (Pattern A; callers may add their own `catchError`).
 * - `loadOnePublic$()` — fetches a public passport without authentication
 *   (Pattern A + Datadog logging).
 * - `loadOnePublicByProductId$()` — resolves a public passport via its IEC
 *   61406 Product ID (full canonical URI, forwarded verbatim) instead of a
 *   UUID (Pattern A + Datadog logging).
 * - `loadList$()` — fetches a paginated, filtered list of passports (Pattern A).
 * - `exportPass$()` — exports a passport as JSON or CSV text; returns an
 *   empty string on failure (Pattern C — explicit silent failure).
 * - `create$()` — creates a new passport and initialises internal state signals.
 * - `createChildren$()` — bulk-creates child passports for a master passport.
 * - `update$()` — patches a passport; emits `onPassportUpdateSuccess` on
 *   success and `onUnprocessableEntityError` on HTTP 422 (Pattern B, re-throw).
 * - `updateStatus$()` — changes the lifecycle status of a passport with the
 *   same HTTP 422 handling as `update$()`.
 * - `restore$()` — restores a passport to its last saved state.
 * - `delete$()` — permanently deletes a passport.
 * - `saveAttributeAssignments$()` — patches attribute assignments; errors
 *   propagate to the global handler (Pattern A).
 * - `loadAnalytics$()` — fetches passport analytics data, optionally filtered by
 *   `schemaType` and `schemaVersion` (Pattern A).
 * - `interactionHistory$()` — retrieves ordered audit-log entries.
 * - `interactionHistoryChanges$()` — retrieves the diff between two audit-log
 *   snapshots.
 * - `loadMappedSchema$()` / `loadSchema$()` / `loadSchemaList$()` — schema
 *   lookups; `loadSchema$` delegates HTTP caching and in-flight deduplication
 *   to `RequestCacheService` / `ApiClient.getShared()`. `loadMappedSchema$`
 *   deliberately re-runs its (cheap) in-memory mapping step on every call so
 *   required-field state always reflects the latest passport data — see its
 *   own doc comment for details.
 * - `loadPassportAncestor$()` / `loadPassportDescendants$()` — hierarchy
 *   navigation.
 * - `addPassportAncestor$()` / `deletePassportAncestor$()` — hierarchy
 *   mutation.
 * - Deprecated Promise wrappers are kept for backward compatibility via
 *   `firstValueFrom()`. All `new Promise(async …)` anti-patterns removed.
 */

import {HttpHeaders} from '@angular/common/http';
import {Injectable, inject, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  defer,
  firstValueFrom,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import {datadogLogs} from '@datadog/browser-logs';

import {environment} from '../environments/environment';
import {ProductIdStandard} from '../utils/product-id-url';
import {BatteryAnalyticsDto} from '@product-passport/passport/dto';
import {AssignmentPatchRequestDto} from '../models/battery/AssignmentPatchDto';
import {BatteryPass, BatteryPassDtoType} from '../models/BatteryPass';
import {BatteryPassAttributeLabel} from '../models/BatteryPassAttributeLabel';
import {
  ApiError,
  AppErrorUnion,
  NormalisedApiError,
} from '../models/errors/api-error';
import {
  InteractionHistory,
  InteractionItem,
} from '../models/InteractionHistory';
import {
  InteractionChangeItem,
  InteractionHistoryChange,
} from '../models/InteractionHistoryChange';
import {
  BatteryAncestor,
  ListBattery,
  LoadListParams,
} from '../models/ListBattery';
import {
  SchemaDTO,
  SchemaLanguageResponse,
  SchemaListDTO,
  SchemaTranslationsMap,
  UISchemaDTO,
} from '../models/Schema';
import {FormCategory} from '../models/FormCategory';
import {
  MasterChildStringEnum,
  PassportDto,
  StatusStringEnum,
} from '../models/Passport';
import {ApiClient} from './api-client.service';
import {AssignmentService} from './assignment.service';
import {UiTreeHandlerService} from './ui-tree-handler.service';
import {RequiredFieldPathExtractor} from './required-field-path-extractor';
import {
  ConditionalField,
  clearHiddenConditionalFields,
  collectConditionalFields,
} from '../app/shared/utils/visible-if';

/** HTTP status for unprocessable entity (validation) errors. */
const HTTP_STATUS_UNPROCESSABLE_ENTITY = 422;

type IdentifierContainer =
  | {
      identifier?: {
        uniqueBatteryPassportIdentifier?: {
          uniqueBatteryPassportIdentifierValue?: unknown;
        };
      };
    }
  | undefined;

/** Removes the backend-generated, read-only identifier value (rejected with 422 if sent back). */
function stripGeneratedIdentifier(container: IdentifierContainer): void {
  delete container?.identifier?.uniqueBatteryPassportIdentifier
    ?.uniqueBatteryPassportIdentifierValue;
}

/** Returned in place of a missing or failing uiSchema so passport load is non-fatal. */
const EMPTY_UI_SCHEMA: UISchemaDTO = {
  data: {
    'ui:favorites': [],
  },
};

/** Base URL for the passport management service. */
const PASSPORT_HOST = environment.backend.passport_host;

/** Base URL for the assignment management service. */
const ASSIGNMENT_HOST = environment.backend.assignment_host;

/**
 * Recursively replaces plain objects whose every value is null or undefined
 * with null. This prevents backend validation failures when an optional
 * sub-object (e.g. operatorInformation) has been partially filled and then
 * fully cleared by the user: without this step the object would still exist
 * in the payload as `{}` or `{ field: null }`, which causes the backend to
 * validate its required sibling attributes and reject the request.
 *
 * Arrays and primitive values are returned unchanged.
 */
function nullifyEmptyObjects(value: unknown): unknown {
  if (
    value === null ||
    value === undefined ||
    typeof value !== 'object' ||
    Array.isArray(value)
  ) {
    return value;
  }
  const processed: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    processed[k] = nullifyEmptyObjects(v);
  }
  const allEmpty = Object.values(processed).every(
    (v) => v === null || v === undefined,
  );
  return allEmpty ? null : processed;
}

/**
 * Builds the PATCH URL for attribute-assignment operations.
 * Mirrors the original logic: `/api` suffix for API type, `/cx` with query
 * param for CX type, base URL otherwise.
 */
function buildAttributeAssignmentsUrl(
  type: 'API' | 'CX',
  skipProvisioning: boolean,
): string {
  const base = `${ASSIGNMENT_HOST}/attribute-assignments`;
  if (type === 'API') {
    return `${base}/api`;
  }
  if (type === 'CX') {
    return `${base}/cx?skipProvisioning=${skipProvisioning}`;
  }
  return base;
}

@Injectable({
  providedIn: 'root',
})
export class PassportService {
  private readonly apiClient = inject(ApiClient);
  private readonly assignmentService = inject(AssignmentService);
  private readonly translate = inject(TranslateService);

  private readonly requiredFieldPathExtractor =
    new RequiredFieldPathExtractor();
  private readonly schemaCache = new Map<string, SchemaDTO>();
  private readonly SCHEMA_CACHE_MAX_SIZE = 50;

  /**
   * Caches the required-field paths per `<passportId>:<schemaId>`,
   * independent of the active language. Required fields depend only on the schema
   * structure and the passport data — never on the UI language — so there is no
   * need to recompute them (and re-run the schema traversal)
   * on every language switch, only when the underlying passport data actually
   * changes. See {@link invalidateRequiredFieldPathsCache}.
   *
   * Bounded to `REQUIRED_FIELD_PATHS_CACHE_MAX_SIZE` entries with oldest-entry
   * eviction (same pattern as `schemaCache`) to prevent unbounded memory growth
   * over long sessions.
   */
  private readonly requiredFieldPathsCache = new Map<string, string[]>();
  private readonly REQUIRED_FIELD_PATHS_CACHE_MAX_SIZE = 50;

  /**
   * Fields with a ui-schema `visibleIf` rule, per schema ID. Populated by
   * {@link loadMappedSchema$} and used by {@link update$} to clear values of
   * fields that became hidden. Bounded by the (small) number of schemas.
   */
  private readonly conditionalFieldsBySchema = new Map<
    string,
    ConditionalField[]
  >();

  readonly passes = signal<{[key: string]: PassportDto | undefined}>({});
  passportIsSaving = signal<boolean>(false);
  originalPasses: {[key: string]: PassportDto | undefined} = {};
  passportsInEdit: {[key: string]: PassportDto | undefined} = {};
  /**
   * Snapshot of the passport as it was last **sent** to the backend for a
   * successful PATCH — i.e. the state loaded from the backend on open, then
   * replaced after every successful PATCH with a clone of that request's
   * outgoing payload (taken before the wire-only field stripping in
   * {@link update$}). Server-generated read-only fields are kept, so they
   * still round-trip cleanly on the next diff.
   *
   * This is the baseline for the auto-save short-circuit in {@link update$}.
   * It must **not** be confused with {@link originalPasses}, which keeps the
   * state at the time the passport was opened (used for "discard changes").
   */
  passportInCache: {[key: string]: PassportDto | undefined} = {};

  /**
   * Emits the normalized validation error received from the server on HTTP 422.
   * Consumers (form directives, workflow components) subscribe to display
   * field-level and category-level error indicators.
   * Emits `null` when a subsequent update succeeds.
   */
  onUnprocessableEntityError = new BehaviorSubject<NormalisedApiError | null>(
    null,
  );

  /** Emits the updated passport DTO after a successful PATCH or PUT. */
  onPassportUpdateSuccess = new BehaviorSubject<PassportDto | null>(null);

  /**
   * Resets the global validation-error / update-success state back to "no
   * errors". `onUnprocessableEntityError` and `onPassportUpdateSuccess` are
   * singleton `BehaviorSubject`s (this service is `providedIn: 'root'`), so
   * they replay their last value to any new subscriber — including form
   * fields and category tabs belonging to a *different* passport that is
   * opened afterward.
   *
   * Callers **must** invoke this when leaving a passport's edit view (e.g.
   * `ngOnDestroy` of the hosting component/directive) so a subsequently
   * opened passport never inherits stale error state from the previous one.
   */
  resetValidationErrors(): void {
    if (this.onUnprocessableEntityError.value !== null) {
      this.onUnprocessableEntityError.next(null);
    }
    if (this.onPassportUpdateSuccess.value !== null) {
      this.onPassportUpdateSuccess.next(null);
    }
  }

  // ─── Observable API (new) ────────────────────────────────────────────────────

  /**
   * Fetches a single passport by ID and populates the internal edit / cache
   * signals. Errors propagate automatically to `GlobalErrorHandlerService`
   * (Pattern A). Callers that need local error handling add their own
   * `catchError` to the returned Observable.
   *
   * @param id   Passport identifier.
   * @param type Battery-passport DTO variant (defaults to `cx_battery_6.0.0`).
   */
  loadOne$(
    id: string,
    type: BatteryPassDtoType = 'cx_battery_6.0.0',
  ): Observable<PassportDto> {
    const params = type ? `?type=${type}` : '';
    return this.apiClient
      .get<PassportDto>(`${PASSPORT_HOST}/passports/${id}${params}`)
      .pipe(
        tap((passport) => {
          passport.data = passport.data || {};
          this.originalPasses[id] = structuredClone(passport); // needed for restoring a pass
          this.passportInCache[id] = structuredClone(passport); // needed to track changes in the pass
          this.passportsInEdit[id] = structuredClone(passport); // source for forms
          this.passes.set(this.passportsInEdit);
          this.invalidateRequiredFieldPathsCache(id);
        }),
      );
  }

  /**
   * Fetches a public passport (no authentication required).
   * Logs a Datadog info event on success.
   */
  loadOnePublic$(id: string): Observable<PassportDto> {
    return this.apiClient
      .get<PassportDto>(`${PASSPORT_HOST}/passports/public/${id}`)
      .pipe(
        tap((passport) => {
          datadogLogs.logger.info(`public battery pass loaded: ${id}`);
          this.passportsInEdit[id] = structuredClone(passport);
          this.passes.set({...this.passportsInEdit});
          this.invalidateRequiredFieldPathsCache(id);
        }),
      );
  }

  /**
   * Resolves a public passport via its Product ID (the full canonical URI,
   * forwarded verbatim). Logs a Datadog info event on success.
   *
   * The backend route is named `iec61406` for historical reasons; it resolves
   * any identifier URI by exact string match, so GS1 Digital Links use the
   * same call (ADR-074 amendment, BL-6190).
   *
   * @param productId The full canonical Product ID URI (e.g.
   *                  `window.location.href`), forwarded verbatim as the
   *                  `productId` query parameter.
   * @param standard  The identifier standard detected from the URL path
   *                  (`iec61406` or `gs1`); only used for logging.
   */
  loadOnePublicByProductId$(
    productId: string,
    standard: ProductIdStandard = 'iec61406',
  ): Observable<PassportDto> {
    return this.apiClient
      .get<PassportDto>(
        `${PASSPORT_HOST}/passports/public/iec61406?productId=${encodeURIComponent(productId)}`,
      )
      .pipe(
        tap((passport) => {
          datadogLogs.logger.info(
            `public battery pass loaded via ${standard} product ID: ${passport.id}`,
          );
          this.passportsInEdit[passport.id] = structuredClone(passport);
          this.passes.set({...this.passportsInEdit});
          this.invalidateRequiredFieldPathsCache(passport.id);
        }),
      );
  }

  /**
   * Fetches a paginated, filtered list of passports.
   * Uses the shared `setTypeFilter` / `setStatusFilter` helpers so the
   * existing filter logic is preserved exactly.
   */
  loadList$(params: LoadListParams = {}): Observable<ListBattery> {
    return this.apiClient.get<ListBattery>(
      `${PASSPORT_HOST}/passports?${this.buildListQueryParams(params)}`,
    );
  }

  /**
   * Exports a passport as JSON or CSV text.
   * Returns an empty string when the request fails (Pattern C — explicit
   * silent failure; the caller receives `''` and can abort silently).
   */
  exportPass$(id: string, type: string): Observable<string> {
    const acceptType = type === 'json' ? 'application/json' : 'text/csv';
    const headers = new HttpHeaders().set('Accept', acceptType);
    return this.apiClient
      .getText(`${PASSPORT_HOST}/passports/${id}/export`, {headers})
      .pipe(catchError(() => of('')));
  }

  /**
   * Creates a new passport and initializes all internal state entries.
   * Sets `passportIsSaving` to `true` before the request.
   */
  create$(
    masterChildFlag: MasterChildStringEnum,
    schemaId: string | undefined,
  ): Observable<PassportDto> {
    this.passportIsSaving.set(true);
    return this.apiClient
      .post<PassportDto>(`${PASSPORT_HOST}/passports`, {
        metadata: {masterChildFlag, schemaId},
      })
      .pipe(
        tap((emptyPass) => {
          this.originalPasses[emptyPass.id] = structuredClone(emptyPass); // needed for restoring a pass
          this.passportInCache[emptyPass.id] = structuredClone(emptyPass); // needed to track changes in the pass
          this.passportsInEdit[emptyPass.id] = structuredClone(emptyPass); // source for forms
          this.passes.set(this.passportsInEdit);
          this.invalidateRequiredFieldPathsCache(emptyPass.id);
        }),
      );
  }

  /**
   * Bulk-creates child passports for a master passport.
   * Both `masterPassportId` and `quantity` are optional query parameters.
   *
   * @remarks REFACTOR: The actual backend response is
   * `{passports: Array<{id: string; data: object}> | null}`, not `BatteryPass`.
   * The return type is incorrect and should be updated once callers are migrated
   * away from the `BatteryPass` assumption.
   * See: `PassportsController.createChildren` in data-validation-and-persistence-service.
   */
  createChildren$(
    masterPassportId?: string,
    quantity?: number,
  ): Observable<BatteryPass> {
    const queryParams = new URLSearchParams();
    if (masterPassportId) {
      queryParams.set('masterPassportId', masterPassportId);
    }
    if (quantity) {
      queryParams.set('quantity', quantity.toString());
    }
    return this.apiClient
      .post<{
        passports: BatteryPass;
      }>(`${PASSPORT_HOST}/passports/children?${queryParams.toString()}`)
      .pipe(map(({passports}) => passports));
  }

  /**
   * Patches a passport (Pattern B).
   *
   * On success:
   * - emits the updated DTO via `onPassportUpdateSuccess`
   * - clears `onUnprocessableEntityError` (resets field errors in the UI)
   * - updates `passportInCache` for future change detection
   * - resets `passportIsSaving` to `false`
   *
   * On HTTP 422:
   * - emits the `NormalisedApiError` via `onUnprocessableEntityError`
   * - resets `passportIsSaving` to `false`
   * - re-throws the error so that the component can also react
   *
   * Skips the HTTP request when the payload is identical to the last
   * successfully persisted state and resolves immediately with the current
   * data object.
   */
  update$(
    id: string,
    data: PassportDto,
    attributeLabelData: BatteryPassAttributeLabel[] | undefined,
  ): Observable<PassportDto> {
    this.passportIsSaving.set(true);
    this.passes.set({...this.passportsInEdit});
    if (attributeLabelData && attributeLabelData.length > 0) {
      this.assignmentService.createAssignments(attributeLabelData, id);
    }

    return this.ensureConditionalFields$(data.metadata?.schemaId, id).pipe(
      switchMap(() => this.patchPassport$(id, data)),
    );
  }

  private patchPassport$(
    id: string,
    data: PassportDto,
  ): Observable<PassportDto> {
    // Hidden conditional values are cleared on a copy, so a failed save does
    // not lose them from the edit model.
    const sendData = structuredClone(data);
    this.clearHiddenConditionalValues(id, sendData);

    if (!this.hasUnpersistedChanges(id, sendData)) {
      this.passportIsSaving.set(false);
      return of(data);
    }

    // Snapshot of exactly what is being sent, taken *before* the request so
    // that edits made while the request is in flight are not mistaken for
    // already-persisted state.
    const persistedSnapshot = structuredClone(sendData);

    // Delete batteryPassportIdentifier (legacy, schema < 1.4.0) and the nested
    // uniqueBatteryPassportIdentifierValue leaf (schema >= 1.4.0, under
    // `generalProductInformation.identifier`, and schema >= 2.0.0, under
    // `dppHeader.identifier`) — all are entirely backend-generated/read-only
    // and rejected with a 422 READONLY_FIELD_VIOLATION if sent back to the
    // backend.
    //
    // Only the `uniqueBatteryPassportIdentifierValue` leaf is removed —
    // its sibling `lastUpdate` is intentionally kept and sent back
    // unchanged: `lastUpdate` is not enforced as read-only server-side, so
    // simply echoing back whatever value was last loaded is harmless (the
    // backend never rejects it), and keeping it in the payload means
    // `identifier.uniqueBatteryPassportIdentifier`
    // still holds real, non-empty content even before the user has filled in
    // any of `identifier`'s other required sibling fields (e.g.
    // `uniqueBatteryIdentifier`). This avoids `nullifyEmptyObjects()` below
    // ever mistaking a still-untouched `identifier` object for one the user
    // intentionally cleared and collapsing it to an explicit `identifier:
    // null` — which previously caused the backend to wipe the
    // server-generated identifier from the database on the very next save
    // of a partially filled draft.
    //
    // Clone the payload so the in-memory model is not mutated.
    const patchPayload: PassportDto = structuredClone(sendData);
    delete patchPayload.data?.generalProductInformation
      ?.batteryPassportIdentifier;

    stripGeneratedIdentifier(
      patchPayload.data?.generalProductInformation as IdentifierContainer,
    );
    stripGeneratedIdentifier(
      (patchPayload.data as {dppHeader?: IdentifierContainer} | undefined)
        ?.dppHeader,
    );

    // Replace empty sub-objects with null so the backend does not validate
    // required fields inside optional objects that the user has cleared.
    if (patchPayload.data) {
      patchPayload.data = nullifyEmptyObjects(patchPayload.data);
    }

    return this.apiClient
      .patch<PassportDto>(`${PASSPORT_HOST}/passports/${id}`, patchPayload)
      .pipe(
        tap((bp) => {
          datadogLogs.logger.info(`battery pass updated: ${id}`);
          this.onPassportUpdateSuccess.next(bp);
          this.onUnprocessableEntityError.next(null);
          // Must run before the baseline below is replaced.
          this.clearHiddenConditionalValues(id, data);
          this.passes.set({...this.passportsInEdit});
          this.passportInCache[id] = persistedSnapshot;
          this.passportIsSaving.set(false);
          this.invalidateRequiredFieldPathsCache(id);
        }),
        catchError((err: AppErrorUnion) => {
          this.passportIsSaving.set(false);
          if (
            err instanceof ApiError &&
            err.status === HTTP_STATUS_UNPROCESSABLE_ENTITY
          ) {
            this.onUnprocessableEntityError.next(err.normalisedError);
          }
          return throwError(() => err);
        }),
      );
  }

  /**
   * Changes the lifecycle status of a passport (Pattern B).
   * Applies the same HTTP 422 handling as `update$()`.
   */
  updateStatus$(id: string, status: StatusStringEnum): Observable<PassportDto> {
    this.passportIsSaving.set(true);
    return this.apiClient
      .put<PassportDto>(`${PASSPORT_HOST}/passports/${id}/status`, {status})
      .pipe(
        tap((bp) => {
          datadogLogs.logger.info(
            `battery pass ${id} status updated: ${status}`,
          );
          this.onPassportUpdateSuccess.next(bp);
          this.passportIsSaving.set(false);
        }),
        catchError((err: AppErrorUnion) => {
          this.passportIsSaving.set(false);
          if (
            err instanceof ApiError &&
            err.status === HTTP_STATUS_UNPROCESSABLE_ENTITY
          ) {
            this.onUnprocessableEntityError.next(err.normalisedError);
          }
          return throwError(() => err);
        }),
      );
  }

  /** Restores a passport to its last saved state. */
  restore$(id: string): Observable<PassportDto> {
    return this.apiClient
      .patch<PassportDto>(`${PASSPORT_HOST}/passports/${id}/restore`, {})
      .pipe(
        tap(() => {
          datadogLogs.logger.info(`battery pass restored: ${id}`);
          this.invalidateRequiredFieldPathsCache(id);
        }),
      );
  }

  /** Permanently deletes a passport. */
  delete$(id: string): Observable<void> {
    return this.apiClient
      .delete<void>(`${PASSPORT_HOST}/passports/${id}`)
      .pipe(tap(() => datadogLogs.logger.info('passport deleted: ' + id)));
  }

  /**
   * Patches attribute assignments for a passport.
   * Errors propagate to the global handler (Pattern A). Callers that need
   * silent failure should add `catchError(() => of(undefined))` themselves.
   */
  saveAttributeAssignments$(
    assignments: AssignmentPatchRequestDto,
    type: 'API' | 'CX',
    skipProvisioning: boolean,
  ): Observable<void> {
    return this.apiClient.patch<void>(
      buildAttributeAssignmentsUrl(type, skipProvisioning),
      assignments,
    );
  }

  /**
   * Fetches passport analytics data.
   *
   * @param schemaType    Optional schema type to filter by (e.g. `'battery'`).
   * @param schemaVersion Optional semver schema version to filter by.
   */
  loadAnalytics$(
    schemaType?: string,
    schemaVersion?: string,
  ): Observable<BatteryAnalyticsDto> {
    const params = new URLSearchParams();
    if (schemaType) {
      params.set('schemaType', schemaType);
    }
    if (schemaVersion) {
      params.set('schemaVersion', schemaVersion);
    }
    const query = params.toString();
    const url = query
      ? `${PASSPORT_HOST}/passports/analytics?${query}`
      : `${PASSPORT_HOST}/passports/analytics`;
    return this.apiClient.get<BatteryAnalyticsDto>(url);
  }

  /** Fetches the ordered audit-log entries for a passport. */
  interactionHistory$(id: string): Observable<InteractionItem[]> {
    return this.apiClient
      .get<InteractionHistory>(`${PASSPORT_HOST}/passports/${id}/auditLogs`)
      .pipe(map((interactions) => interactions.data));
  }

  /** Fetches the diff between two audit-log snapshots. */
  interactionHistoryChanges$(
    passId: string,
    originId: string,
    targetId: string,
  ): Observable<InteractionChangeItem[]> {
    return this.apiClient
      .get<InteractionHistoryChange>(
        `${PASSPORT_HOST}/passports/${passId}/auditLogs/${originId}/${targetId}`,
      )
      .pipe(map((interactions) => interactions.data));
  }

  /**
   * Fetches the mapped schema for a passport.
   * Uses `UiTreeHandlerService.mapUiTreeToFormCategories` when a ui:tree exists.
   *
   * Deliberately NOT cached via `RequestCacheService`: the mapped
   * `FormCategory[]` bakes in `isRequired` flags computed from the *current*
   * passport data (via `allOf`/`if`/`then` conditions, e.g. battery category).
   * A synthetic cache key that doesn't fully capture that live data (schema +
   * language only) previously caused stale required-field markers to survive
   * passport data changes — e.g. switching a battery's category from "ev" to
   * "lmt" kept an EV-only field marked as required until a full page reload.
   * Every call therefore re-runs the mapping so required-field state always
   * reflects the latest passport data. The underlying HTTP calls
   * (`loadUiSchema$`, `loadSchema$`, schema translations) still go through
   * `ApiClient.getShared()` / `RequestCacheService`, so this does not cause
   * redundant network requests — only the (cheap) in-memory mapping step is
   * re-executed.
   */
  loadMappedSchema$(
    schemaId: string,
    passportId?: string,
  ): Observable<FormCategory[]> {
    const languageCode = this.resolveActiveLanguageCode();
    // Step 1: Load UI schema from dedicated endpoint.
    return this.loadUiSchema$(schemaId).pipe(
      catchError(() => of(EMPTY_UI_SCHEMA)),
      switchMap((uiSchema) => {
        // Step 2: Load schema for validation/fallback metadata.
        // A schema API failure must not crash the form — proceed with
        // undefined so mapUiTreeToFormCategories renders fields without
        // fallback metadata rather than killing the whole chain.
        return this.loadSchema$(schemaId).pipe(
          catchError((err) => {
            datadogLogs.logger.error('Failed to load schema', {
              schemaId,
              error: err instanceof Error ? err.message : String(err),
            });
            return of(undefined);
          }),
          map((schema) => ({schema, uiSchema})),
        );
      }),

      switchMap(({schema, uiSchema}) => {
        if (UiTreeHandlerService.isEmpty(uiSchema)) {
          return of([]);
        }
        // If passportId provided, compute required paths from actual passport data.
        // Cached per <passportId>:<schemaId> (language-independent) so that a
        // language switch does not re-run the (potentially expensive)
        // required-field computation unnecessarily.
        let requiredPaths: string[] | undefined;
        if (passportId && schema) {
          const passport = this.passes()[passportId];
          if (passport) {
            requiredPaths = this.getCachedRequiredFieldPaths(
              schemaId,
              passportId,
              schema,
              passport.data,
            );
          }
        }
        return this.loadSchemaTranslations$(schemaId, languageCode).pipe(
          map((schemaTranslations) =>
            UiTreeHandlerService.mapUiTreeToFormCategories(
              uiSchema,
              schema,
              schemaTranslations,
              requiredPaths,
            ),
          ),
          tap((categories) =>
            this.conditionalFieldsBySchema.set(
              schemaId,
              collectConditionalFields(categories),
            ),
          ),
        );
      }),
    );
  }

  private loadSchemaTranslations$(
    schemaId: string,
    languageCode = 'en',
  ): Observable<SchemaTranslationsMap> {
    if (!schemaId) {
      return of({});
    }

    return this.apiClient
      .getShared<SchemaLanguageResponse>(
        `${PASSPORT_HOST}/definitions/schema/${schemaId}/languages/${languageCode}`,
      )
      .pipe(
        map((response) => response.data?.translations ?? {}),
        catchError(() => of({})),
      );
  }

  private resolveActiveLanguageCode(): string {
    return (
      this.translate.currentLang || this.translate.getDefaultLang() || 'en'
    );
  }

  /**
   * Fetches the UI schema for a given schema ID from the backend API.
   * Callers that need a non-fatal fallback (e.g. `loadMappedSchema$`) apply
   * `catchError` on the returned observable rather than here, so the error
   * propagates by default and can be handled at the appropriate call site.
   */
  loadUiSchema$(schemaId: string): Observable<UISchemaDTO> {
    return this.apiClient.getShared<UISchemaDTO>(
      `${PASSPORT_HOST}/definitions/schema/${schemaId}/ui-schema`,
    );
  }

  /**
   * Fetches a schema by ID (cached via RequestCacheService / apiClient.getShared).
   * Concurrent calls share one in-flight HTTP request; the response is replayed
   * to late subscribers. The entry expires after the default TTL (5 minutes).
   */
  loadSchema$(id: string): Observable<SchemaDTO> {
    return this.apiClient
      .getShared<SchemaDTO>(`${PASSPORT_HOST}/definitions/schema/${id}`)
      .pipe(
        tap((schema) => {
          if (this.schemaCache.size >= this.SCHEMA_CACHE_MAX_SIZE) {
            const oldest = this.schemaCache.keys().next().value;
            if (oldest !== undefined) {
              this.schemaCache.delete(oldest);
            }
          }
          this.schemaCache.set(id, schema);
        }),
      );
  }

  /**
   * Returns the cached SchemaDTO for the given schema ID, or `null` when the
   * schema has not yet been loaded. This allows synchronous callers to access
   * the schema without subscribing to an Observable.
   */
  getSchemaSync(id: string): SchemaDTO | null {
    return this.schemaCache.get(id) ?? null;
  }

  /**
   * Returns the required field paths for the given schema evaluated against live passport data.
   *
   * Strips the `$schema` meta-property before validation, then delegates to
   * `RequiredFieldPathExtractor` (`@cfworker/json-schema`-based, CSP-safe) which
   * walks `required`/`properties` and evaluates every `allOf` conditional block
   * against `controllingData`.
   *
   * @param schema           The fully-inlined SchemaDTO returned by `loadSchema$`.
   * @param controllingData  The live passport data object (or any subset of it) that
   *                         the schema's `allOf` conditions reference. Pass the full
   *                         passport object so every conditional field is resolved.
   * @returns Slash-delimited paths for all required fields active under the given data,
   *          e.g. `['/generalProductInformation/name', '/performance/stateOfCertifiedEnergy']`.
   */
  getRequiredFieldPaths(schema: SchemaDTO, controllingData: unknown): string[] {
    const resolvedSchema = {...(schema.data.schema as Record<string, unknown>)};
    delete resolvedSchema['$schema'];
    try {
      return this.requiredFieldPathExtractor.extractRequiredFieldsFromSchema(
        resolvedSchema,
        controllingData,
      );
    } catch (error) {
      // Defensive guard: should the schema traversal throw for any reason (e.g. a
      // malformed schema), fail soft instead of breaking the form builder — this
      // only disables the required-field progress calculation, it does not affect
      // form usability otherwise.
      datadogLogs.logger.error(
        'Failed to extract required field paths (schema validation error)',
        {
          schemaId: schema.data.id,
          error: error instanceof Error ? error.message : String(error),
        },
      );
      return [];
    }
  }

  /**
   * Returns the required field paths for `schemaId`/`passportId`, reusing a
   * previously computed result when available.
   *
   * Required fields depend only on the JSON schema structure and the passport's
   * data (via `allOf`/`if`/`then` conditions) — never on the currently active
   * UI language. `loadMappedSchema$()` is cached per `<schemaId>:<language>`,
   * so switching the language always causes a cache miss and would otherwise
   * re-run this computation for no reason. Caching it separately,
   * keyed by `<passportId>:<schemaId>`, means the (potentially expensive)
   * computation only runs once per passport/schema combination
   * and is reused across language switches.
   *
   * The cache entry is invalidated via {@link invalidateRequiredFieldPathsCache}
   * whenever the underlying passport data is (re)loaded or successfully saved.
   */
  private getCachedRequiredFieldPaths(
    schemaId: string,
    passportId: string,
    schema: SchemaDTO,
    controllingData: unknown,
  ): string[] {
    const cacheKey = `${passportId}:${schemaId}`;
    const cached = this.requiredFieldPathsCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    const computed = this.getRequiredFieldPaths(schema, controllingData);
    if (
      this.requiredFieldPathsCache.size >=
      this.REQUIRED_FIELD_PATHS_CACHE_MAX_SIZE
    ) {
      const oldest = this.requiredFieldPathsCache.keys().next().value;
      if (oldest !== undefined) {
        this.requiredFieldPathsCache.delete(oldest);
      }
    }
    this.requiredFieldPathsCache.set(cacheKey, computed);
    return computed;
  }

  /**
   * Clears all cached required-field-path entries for the given passport
   * (across all schemas), forcing the next `loadMappedSchema$()` call to
   * recompute them from the current passport data.
   *
   * Called whenever `passportId`'s data is (re)loaded or successfully
   * persisted, since that is the only time required-field results can
   * legitimately change.
   */
  private invalidateRequiredFieldPathsCache(passportId: string): void {
    const prefix = `${passportId}:`;
    for (const key of this.requiredFieldPathsCache.keys()) {
      if (key.startsWith(prefix)) {
        this.requiredFieldPathsCache.delete(key);
      }
    }
  }

  /** Fetches the full schema list. */
  loadSchemaList$(): Observable<SchemaListDTO> {
    return this.apiClient.get<SchemaListDTO>(
      `${PASSPORT_HOST}/definitions/schema`,
    );
  }

  /**
   * Loads the ancestor of a passport.
   *
   * @param passportId The ID of the passport.
   * @returns Observable emitting the ancestor data.
   */
  loadPassportAncestor$(passportId: string): Observable<BatteryAncestor> {
    return this.apiClient.get<BatteryAncestor>(
      `${PASSPORT_HOST}/passports/${passportId}/ancestor`,
    );
  }

  /**
   * Loads the descendants of a passport.
   *
   * @param passportId The ID of the passport.
   * @returns Observable emitting the descendant list.
   */
  loadPassportDescendants$(passportId: string): Observable<ListBattery> {
    return this.apiClient.get<ListBattery>(
      `${PASSPORT_HOST}/passports/${passportId}/descendants`,
    );
  }

  /**
   * Adds an ancestor to a passport.
   *
   * This operation fails if:
   * - the `ancestorPassportId` or `passportId` is not an Individual (Child)
   *   passport
   * - `ancestorPassportId` and `passportId` do not belong to the same
   *   organization
   * - `ancestorPassportId` is already a descendant of `passportId`
   *
   * @param passportId         The ID of the passport.
   * @param ancestorPassportId The ID of the ancestor passport.
   * @returns Observable emitting `true` on success.
   */
  addPassportAncestor$(
    passportId: string,
    ancestorPassportId: string,
  ): Observable<true> {
    return this.apiClient
      .put<void>(`${PASSPORT_HOST}/passports/${passportId}/ancestor`, {
        ancestorPassportId,
      })
      .pipe(map(() => true as const));
  }

  /**
   * Deletes the ancestor of a passport.
   *
   * @param passportId The ID of the passport.
   * @returns Observable emitting `true` on success.
   */
  deletePassportAncestor$(passportId: string): Observable<true> {
    return this.apiClient
      .delete<void>(`${PASSPORT_HOST}/passports/${passportId}/ancestor`)
      .pipe(map(() => true as const));
  }

  /**
   * Whether the passport differs from the state it had when it was opened.
   * Used by the edit view to decide whether discarding changes needs a
   * confirmation — **not** for the auto-save short-circuit.
   */
  passportChanged(id: string): boolean {
    const original = this.originalPasses[id];
    const edited = this.passes()[id];

    return (
      original !== undefined &&
      edited !== undefined &&
      JSON.stringify(original) !== JSON.stringify(edited)
    );
  }

  /**
   * Makes sure the conditional (`visibleIf`) fields of the schema are known
   * before saving, so hidden values are cleared even if the form's mapped
   * schema was never loaded in this session. A failing load is non-fatal.
   */
  private ensureConditionalFields$(
    schemaId: string | undefined,
    passportId: string,
  ): Observable<void> {
    if (!schemaId || this.conditionalFieldsBySchema.has(schemaId)) {
      return of(undefined);
    }
    return defer(() => this.loadMappedSchema$(schemaId, passportId)).pipe(
      take(1),
      map(() => {
        // An empty ui schema yields no categories and registers nothing.
        if (!this.conditionalFieldsBySchema.has(schemaId)) {
          this.conditionalFieldsBySchema.set(schemaId, []);
        }
      }),
      catchError(() => of(undefined)),
    );
  }

  /**
   * Clears (sets to `null`) the values of `visibleIf` fields that are hidden in
   * `data` and were visible — or have been edited — since the last persisted
   * state, so the PATCH removes them instead of persisting a stale value
   * (e.g. `repairStatus` once `batteryStatusValue` is no longer `re-used`).
   * Mutates `data.data` in place.
   */
  private clearHiddenConditionalValues(id: string, data: PassportDto): void {
    const schemaId = data.metadata?.schemaId;
    const fields = schemaId
      ? this.conditionalFieldsBySchema.get(schemaId)
      : undefined;
    if (!fields?.length || !data.data) {
      return;
    }
    // Without a persisted baseline, fall back to the loaded snapshot so
    // untouched stale values are not mistaken for edits.
    clearHiddenConditionalFields(
      fields,
      (this.passportInCache[id] ?? this.originalPasses[id])?.data,
      data.data,
    );
  }

  /**
   * Whether `data` differs from the last state that was successfully persisted
   * for this passport.
   *
   * The auto-save baseline has to be the *last persisted* state, not the state
   * at load time ({@link originalPasses}): otherwise, reverting a field back to
   * its originally loaded value after an intermediate save (e.g. deleting the
   * last character — which is persisted — and typing it again) would look
   * "unchanged" and the restoring PATCH would never be sent, leaving the
   * backend with the intermediate value.
   *
   * Returns `true` when no baseline exists yet, so a passport that has never
   * been loaded/persisted through this service is always sent.
   */
  private hasUnpersistedChanges(id: string, data: PassportDto): boolean {
    const persisted = this.passportInCache[id];

    return (
      persisted === undefined ||
      JSON.stringify(persisted) !== JSON.stringify(data)
    );
  }

  setTypeFilter(params: LoadListParams, queryParams: URLSearchParams) {
    if (params.type) {
      if (params.type.master && !params.type.child) {
        queryParams.set('type', 'MASTER');
      } else if (!params.type.master && params.type.child) {
        queryParams.set('type', 'CHILD');
      } else {
        queryParams.delete('type');
      }
    }
  }

  setStatusFilter(params: LoadListParams, queryParams: URLSearchParams) {
    if (params.passportStatus) {
      if (params.passportStatus.done && !params.passportStatus.draft) {
        queryParams.set('passportStatus', 'complete');
      } else if (!params.passportStatus.done && params.passportStatus.draft) {
        queryParams.set('passportStatus', 'draft');
      } else {
        queryParams.delete('passportStatus');
      }
    }
  }

  // ─── Deprecated Promise wrappers ─────────────────────────────────────────────

  /**
   * @deprecated Use `loadOne$()` instead.
   * The `haveOwnErrorHandling` parameter is ignored — errors now propagate
   * through the `errorResponseInterceptor` and reach the caller's `catchError`
   * or `GlobalErrorHandlerService`.
   */
  async loadOne(
    id: string,
    _haveOwnErrorHandling = false,
    type: BatteryPassDtoType = 'cx_battery_6.0.0',
  ): Promise<PassportDto> {
    return firstValueFrom(this.loadOne$(id, type));
  }

  /** @deprecated Use `loadOnePublic$()` instead. */
  async loadOnePublic(id: string): Promise<PassportDto> {
    return firstValueFrom(this.loadOnePublic$(id));
  }

  /** @deprecated Use `loadList$()` instead. */
  async loadList(params: LoadListParams = {}): Promise<ListBattery> {
    return firstValueFrom(this.loadList$(params));
  }

  /** @deprecated Use `exportPass$()` instead. */
  async exportPass(id: string, type: string): Promise<string> {
    return firstValueFrom(this.exportPass$(id, type));
  }

  /** @deprecated Use `create$()` instead. */
  async create(
    masterChildFlag: MasterChildStringEnum,
    schemaId: string | undefined,
  ): Promise<PassportDto> {
    return firstValueFrom(this.create$(masterChildFlag, schemaId));
  }

  /** @deprecated Use `createChildren$()` instead. */
  async createChildren(
    masterPassportId?: string,
    quantity?: number,
  ): Promise<BatteryPass> {
    return firstValueFrom(this.createChildren$(masterPassportId, quantity));
  }

  /** @deprecated Use `update$()` instead. */
  async update(
    id: string,
    data: PassportDto,
    attributeLabelData: BatteryPassAttributeLabel[] | undefined,
  ): Promise<PassportDto> {
    return firstValueFrom(this.update$(id, data, attributeLabelData));
  }

  /** @deprecated Use `updateStatus$()` instead. */
  async updateStatus(
    id: string,
    status: StatusStringEnum,
  ): Promise<PassportDto> {
    return firstValueFrom(this.updateStatus$(id, status));
  }

  /** @deprecated Use `restore$()` instead. */
  async restore(id: string): Promise<PassportDto> {
    return firstValueFrom(this.restore$(id));
  }

  /** @deprecated Use `delete$()` instead. */
  async delete(id: string): Promise<boolean> {
    return firstValueFrom(this.delete$(id).pipe(map(() => true)));
  }

  /**
   * @deprecated Use `saveAttributeAssignments$()` instead.
   * The `ownErrorHandling` parameter is no longer meaningful — errors always
   * propagate through the interceptor.
   */
  async saveAttributeAssignments(
    assignments: AssignmentPatchRequestDto,
    type: 'API' | 'CX',
    skipProvisioning: boolean,
    _ownErrorHandling = false,
  ): Promise<unknown> {
    return firstValueFrom(
      this.saveAttributeAssignments$(assignments, type, skipProvisioning).pipe(
        map(() => true),
      ),
    );
  }

  /** @deprecated Use `loadAnalytics$()` instead. */
  async loadAnalytics(
    schemaType?: string,
    schemaVersion?: string,
  ): Promise<BatteryAnalyticsDto> {
    return firstValueFrom(this.loadAnalytics$(schemaType, schemaVersion));
  }

  /** @deprecated Use `interactionHistory$()` instead. */
  async interactionHistory(id: string): Promise<Array<InteractionItem>> {
    return firstValueFrom(this.interactionHistory$(id));
  }

  /** @deprecated Use `loadMappedSchema$()` instead. */
  async loadMappedSchema(
    schemaId: string,
    passportId?: string,
  ): Promise<FormCategory[]> {
    return firstValueFrom(this.loadMappedSchema$(schemaId, passportId));
  }

  /** @deprecated Use `loadSchema$()` instead. */
  async loadSchema(id: string): Promise<SchemaDTO> {
    return firstValueFrom(this.loadSchema$(id));
  }

  /** @deprecated Use `loadSchemaList$()` instead. */
  async loadSchemaList(): Promise<SchemaListDTO> {
    return firstValueFrom(this.loadSchemaList$());
  }

  /**
   * Loads the ancestor of a passport.
   * @deprecated Use `loadPassportAncestor$()` instead.
   */
  async loadPassportAncestor(passportId: string): Promise<BatteryAncestor> {
    return firstValueFrom(this.loadPassportAncestor$(passportId));
  }

  /**
   * Loads the descendants of a passport.
   * @deprecated Use `loadPassportDescendants$()` instead.
   */
  async loadPassportDescendants(passportId: string): Promise<ListBattery> {
    return firstValueFrom(this.loadPassportDescendants$(passportId));
  }

  /**
   * Adds an ancestor to a passport.
   * @deprecated Use `addPassportAncestor$()` instead.
   * The `hasOwnErrorHandling` parameter is no longer meaningful.
   */
  async addPassportAncestor(
    passportId: string,
    ancestorPassportId: string,
    _hasOwnErrorHandling = false,
  ): Promise<boolean> {
    return firstValueFrom(
      this.addPassportAncestor$(passportId, ancestorPassportId),
    );
  }

  /**
   * Deletes the ancestor of a passport.
   * @deprecated Use `deletePassportAncestor$()` instead.
   */
  async deletePassportAncestor(passportId: string): Promise<boolean> {
    return firstValueFrom(this.deletePassportAncestor$(passportId));
  }

  /** @deprecated Use `interactionHistoryChanges$()` instead. */
  async interactionHistoryChanges(
    passId: string,
    originId: string,
    targetId: string,
  ): Promise<Array<InteractionChangeItem>> {
    return firstValueFrom(
      this.interactionHistoryChanges$(passId, originId, targetId),
    );
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  private buildListQueryParams(params: LoadListParams): string {
    const queryParams = new URLSearchParams();
    const entries = Object.entries(params) as [
      string,
      string | number | undefined,
    ][];
    this.setTypeFilter(params, queryParams);
    this.setStatusFilter(params, queryParams);
    entries.forEach(([key, value]) => {
      if (key === 'type' || key === 'passportStatus') {
        return;
      }
      if (value !== undefined && value !== null && value !== '') {
        queryParams.set(key, value.toString());
      }
    });
    return queryParams.toString();
  }
}
